import React, { useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import LoginModal from '../components/LoginModal';

const TrackerGame = () => {
  const { isAuthenticated } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  const handleCreateProfile = () => {
    setShowLoginModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-900 via-green-800 to-green-900 text-white relative overflow-hidden">
      {/* Jungle background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 text-6xl animate-pulse">🌴</div>
        <div className="absolute top-20 right-20 text-5xl animate-bounce">🌿</div>
        <div className="absolute bottom-20 left-1/4 text-4xl animate-pulse">🍃</div>
        <div className="absolute bottom-10 right-1/3 text-5xl animate-bounce">🌱</div>
        <div className="absolute top-1/2 left-1/3 text-4xl animate-pulse">🌳</div>
      </div>
      
      {/* Main content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen">
        <div className="text-center max-w-3xl mx-auto px-6">
          {/* Call to Action for Profile Creation - Moved to top */}
          {!isAuthenticated && (
            <div className="mb-12 bg-black bg-opacity-60 backdrop-blur-sm rounded-lg p-8 border-2 border-yellow-500">
              <div className="text-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-yellow-300">
                  🎯 JOIN THE MISSION
                </h2>
                <p className="text-lg text-yellow-100 mb-6">
                  Create your profile to unlock exclusive features and track your progress!
                </p>
                
                {/* Benefits Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-green-800 bg-opacity-50 rounded-lg p-4 border border-green-500">
                    <div className="text-3xl mb-2">🏆</div>
                    <h3 className="font-bold text-green-300 mb-2">Score Tracking</h3>
                    <p className="text-sm text-green-200">
                      Save and track your game scores across all missions
                    </p>
                  </div>
                  <div className="bg-blue-800 bg-opacity-50 rounded-lg p-4 border border-blue-500">
                    <div className="text-3xl mb-2">📅</div>
                    <h3 className="font-bold text-blue-300 mb-2">Release Tracking</h3>
                    <p className="text-sm text-blue-200">
                      Get notified about new game releases and updates
                    </p>
                  </div>
                  <div className="bg-purple-800 bg-opacity-50 rounded-lg p-4 border border-purple-500">
                    <div className="text-3xl mb-2">🔍</div>
                    <h3 className="font-bold text-purple-300 mb-2">Enhanced Search</h3>
                    <p className="text-sm text-purple-200">
                      Access advanced search features and personalized results
                    </p>
                  </div>
                </div>

                {/* CTA Button */}
                <button 
                  onClick={handleCreateProfile}
                  className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-black px-8 py-4 rounded-lg transition-all duration-200 font-bold text-lg border-2 border-yellow-400 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  🚀 CREATE PROFILE NOW
                </button>
                
                <p className="text-sm text-yellow-200 mt-4">
                  Free to join • No commitment required
                </p>
              </div>
            </div>
          )}

          {/* Already logged in message - Moved to top */}
          {isAuthenticated && (
            <div className="mb-8 text-center">
              <div className="bg-green-800 bg-opacity-50 rounded-lg p-4 border border-green-500">
                <p className="text-green-200 font-semibold">
                  ✅ You're ready for the mission! Your profile is active.
                </p>
              </div>
            </div>
          )}
          
          {/* Main message with jungle theme */}
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600 bg-clip-text text-transparent">
            SURVIVE THE JUNGLE
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-yellow-200 mb-8 font-semibold">
            The Tracker Game is deep in development...
          </p>
          
          {/* Description with jungle theme */}
          <div className="bg-black bg-opacity-50 backdrop-blur-sm rounded-lg p-8 mb-8 border border-yellow-600">
            <p className="text-lg text-yellow-100 leading-relaxed mb-4">
              We're navigating through the dense jungle of code and features to bring you 
              an epic survival tracking experience! This will be a unique game that tests 
              your skills and strategy in the most challenging environments.
            </p>
            <p className="text-md text-yellow-200">
              Stay alert and ready for the ultimate gaming mission! 💪
            </p>
          </div>
          
          {/* Mission progress indicators */}
          <div className="flex justify-center space-x-6 mb-8">
            <div className="flex flex-col items-center">
              <div className="w-5 h-5 bg-green-500 rounded-full mb-2 shadow-lg shadow-green-400"></div>
              <span className="text-sm text-green-300 font-semibold">RECON</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-5 h-5 bg-yellow-500 rounded-full mb-2 animate-pulse shadow-lg shadow-yellow-400"></div>
              <span className="text-sm text-yellow-300 font-semibold">DEPLOYMENT</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-5 h-5 bg-orange-500 rounded-full mb-2 shadow-lg shadow-orange-400"></div>
              <span className="text-sm text-orange-300 font-semibold">TRAINING</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-5 h-5 bg-gray-600 rounded-full mb-2 shadow-lg"></div>
              <span className="text-sm text-gray-400 font-semibold">MISSION</span>
            </div>
          </div>
          
          {/* Action buttons with jungle theme */}
          <div className="flex justify-center space-x-4">
            <button 
              onClick={() => window.history.back()} 
              className="bg-yellow-600 hover:bg-yellow-700 text-black px-6 py-3 rounded-lg transition-colors duration-200 font-bold border-2 border-yellow-400"
            >
              ← RETREAT
            </button>
            <button 
              onClick={() => window.location.href = '/'} 
              className="bg-green-700 hover:bg-green-600 text-white px-6 py-3 rounded-lg transition-colors duration-200 font-bold border-2 border-green-500"
            >
              🏠 BASE CAMP
            </button>
          </div>
          
          {/* Survival tip */}
          <div className="mt-8 text-center">
            <p className="text-sm text-yellow-200 italic">
              "In the jungle, patience is your greatest weapon..."
            </p>
          </div>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        redirectTo="/tracker-game"
      />
    </div>
  );
};

export default TrackerGame; 